import { Fragment, useEffect, useState } from 'react'
import AnimalImage from '../components/AnimalImage.jsx'
import { speak } from '../lib/speech.js'
import { play } from '../lib/sounds.js'

/**
 * The one interstitial (D-099). Two jobs, one screen:
 *
 *   phase "ready"  — right after Fight!. "Let's get ready to rumble!" for at
 *                    least RUMBLE_MIN_MS.ready while the matchup and every fact
 *                    page are built from roster.json (instant) and the model
 *                    starts on the battle behind them. A hype beat, not a wait:
 *                    nothing the kid could see is loading here.
 *   phase "battle" — the battle door, and only when the story is still being
 *                    written when the kid gets there. Replaces a disabled button
 *                    with a screen that says so, and steps aside the moment the
 *                    story lands (App.jsx owns the timing).
 *
 * Errors land here too, with the retry and back controls the old loading
 * screen carried. The screen is never tappable: there is nothing to skip to.
 */
export const RUMBLE_MIN_MS = { ready: 2200, battle: 900 }
/** After this long at the battle door, say the story is still being written. */
const SLOW_MS = 5000

const COPY = {
  ready: { title: "Let's get ready to rumble!", sub: null },
  battle: { title: 'Here they come!', sub: 'The battle is almost ready…' },
}

export default function Rumble({ A, B, phase = 'ready', error = null, age, onRetry, onBack }) {
  const copy = COPY[phase] || COPY.ready
  const pose = phase === 'battle' ? 'fierce' : 'neutral'
  const [slow, setSlow] = useState(false)

  useEffect(() => {
    setSlow(false)
    if (phase !== 'battle') return undefined
    const t = setTimeout(() => setSlow(true), SLOW_MS)
    return () => clearTimeout(t)
  }, [phase])

  // Announce it at the ages that cannot read it (SPEC 7), and let both animals
  // sound off on the way in (D-088). App.jsx silences speech on every screen
  // change, so the line never runs into the matchup's read-aloud.
  useEffect(() => {
    if (error) return undefined
    if (age !== '6') speak(copy.title)
    if (phase !== 'ready') return undefined
    if (A) play(A.sound)
    const t = setTimeout(() => B && play(B.sound), 700)
    return () => clearTimeout(t)
  }, [phase, error]) // eslint-disable-line react-hooks/exhaustive-deps

  const words = copy.title.split(' ')
  return (
    <section className={`screen rumble rumble--${phase}${error ? ' rumble--error' : ''}`} aria-live="polite" aria-busy={!error}>
      <div className="rumble__pair">
        {A && (
          <div className="rumble__side rumble__side--a">
            <AnimalImage slug={A.slug} pose={pose} alt={A.name} />
            <span className="rumble__name rumble__name--a">{A.name}</span>
          </div>
        )}
        {A && B && <div className="rumble__vs" aria-hidden="true">VS</div>}
        {B && (
          <div className="rumble__side rumble__side--b">
            <AnimalImage slug={B.slug} pose={pose} mirror alt={B.name} />
            <span className="rumble__name rumble__name--b">{B.name}</span>
          </div>
        )}
      </div>
      {error ? (
        <>
          <p className="rumble__text rumble__text--error" role="alert">{error}</p>
          <div className="rumble__actions">
            <button className="btn" onClick={onBack}>Pick again</button>
            <button className="btn btn--primary" onClick={onRetry}>Try again</button>
          </div>
        </>
      ) : (
        <>
          <h1 className="rumble__title">
            {/* One span per word so each can land on its own beat; the spaces
                between them are real, so a screen reader and the e2e driver both
                read a sentence rather than one long word. */}
            {words.map((w, i) => (
              <Fragment key={`${phase}-${i}`}>
                {i > 0 && ' '}
                <span style={{ '--i': i }}>{w}</span>
              </Fragment>
            ))}
          </h1>
          <div className="rumble__dots" aria-hidden="true"><i /><i /><i /></div>
          {(copy.sub || slow) && <p className="rumble__sub">{slow ? 'Still writing the story…' : copy.sub}</p>}
        </>
      )}
    </section>
  )
}
